import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenSize, useCrispChat } from '@/hooks';
import { gsap } from 'gsap';
import { Icon } from '@iconify/react';
import bellOutline from '@iconify/icons-fa-solid/bell';
import MenuIcon from '@/components/icons/MenuIcon';

import LoginButton from '@/components/auth/LoginButton';
import RegisterButton from '@/components/auth/RegisterButton';
import Wallet from '@/components/wallet/Wallet';
import ProfileMenu from '@/components/common/ProfileMenu/ProfileMenu';
import TextGold from '@/components/common/TextGold';
import Logo from '@/components/common/Logo/Logo';
import { getUID } from '@/utils';
import styles from './Header.module.css';

/**
 * Header Component
 * Main header with logo, navigation, and user actions
 * Matches old Header-Guest structure for non-logged in users
 */
const Header = ({ onMenuToggle, isSidebarCollapsed }) => {
  const { isMobile, isTablet } = useScreenSize();
  const isMobileOrTablet = isMobile || isTablet;
  const location = useLocation();
  const navigate = useNavigate();
  const { handleSupportClick } = useCrispChat();
  const [showFullLogo, setShowFullLogo] = useState(!isMobile);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const bonusIconRef = useRef(null);

  useEffect(() => {
    setShowFullLogo(!isMobileOrTablet);

    // Check login status
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
    };

    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    return () => window.removeEventListener('storage', checkAuthStatus);
  }, [isMobileOrTablet]);

  // GSAP animation for bonus icon - simple blink only
  useEffect(() => {
    if (bonusIconRef.current) {
      const element = bonusIconRef.current;
      
      // Continuous slow blinking animation
      const blinkTimeline = gsap.timeline({ repeat: -1 });
      blinkTimeline.to(element, {
        opacity: 0.4,
        duration: 2,
        ease: 'power1.inOut',
      });
      blinkTimeline.to(element, {
        opacity: 1,
        duration: 2,
        ease: 'power1.inOut',
      });

      return () => {
        blinkTimeline.kill();
      };
    }
  }, []);

  // Check if current route is sports-related
  const isSportsRoute = location.pathname.startsWith('/sports/') ||
    location.pathname.startsWith('/match/');

  const isWalletRoute = location.pathname === '/wallet';

  // ── Notifications ──
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState({ unread: [], read: [] });
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    const uid = getUID();
    if (!uid) return;
    try {
      const res = await fetch(`https://api.cryptmet.com/firebase/unread-count/${uid}`);
      const data = await res.json();
      if (data.success) setUnreadCount(data.unreadCount || 0);
    } catch { /* silent */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    const uid = getUID();
    if (!uid) return;
    setNotifLoading(true);
    try {
      const res = await fetch(`https://api.cryptmet.com/firebase/history/${uid}`);
      const data = await res.json();
      if (data.success) {
        setNotifications({
          unread: data.notifications?.unread || [],
          read: data.notifications?.read || [],
        });
      }
      await fetchUnreadCount();
    } catch { /* silent */ }
    finally { setNotifLoading(false); }
  }, [fetchUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    const uid = getUID();
    if (!uid || !notifications.unread.length) return;
    try {
      await fetch('https://api.cryptmet.com/firebase/mark-as-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: uid, notificationIds: notifications.unread.map((n) => n.id) }),
      });
      setNotifications((prev) => ({
        unread: [],
        read: [...prev.read, ...prev.unread.map((n) => ({ ...n, is_read: true }))],
      }));
      setUnreadCount(0);
    } catch { /* silent */ }
  }, [notifications.unread]);

  // Fetch on mount + poll every 30s
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchUnreadCount();
    fetchNotifications();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchUnreadCount, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {!isMobileOrTablet && (
            <button
              className={styles.menuButton}
              onClick={onMenuToggle}
              aria-label="Toggle sidebar"
            >
              <MenuIcon size="24px" />
            </button>
          )}

          <Link to="/" className={styles.logoLink}>
            <Logo size={isMobileOrTablet ? 'small' : 'default'} />
          </Link>
          {isWalletRoute && (
            <TextGold as="span" className={styles.walletTitle}>Wallet</TextGold>
          )}
        </div>

        {/* Center Section - Wallet (for logged in users, mobile & tablet) */}
        {isLoggedIn && isMobileOrTablet && (
          <div className={styles.centerSection}>
            <Wallet />
          </div>
        )}

        {/* Right Section */}
        <div className={styles.rightSection}>
          {/* Redeem & Rakeback icon (desktop only) */}
         {
          isLoggedIn && !isMobileOrTablet && (
            <div
            // ref={bonusIconRef}
            onClick={() => navigate('/gift-cards')}
            aria-label="Redeem & Rakeback"
            className={styles.bonusIconButton}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/assets/images/bonus/2.webp"
              alt="Redeem & Rakeback"
              className={styles.bonusIconImage}
              loading="lazy"
            />
          </div>
          )
         }

          {/* Notification Bell (logged in only) */}
          {isLoggedIn && (
            <div className={styles.notifWrap} ref={notifRef}>
              <button
                className={styles.notifButton}
                onClick={() => {
                  if (isMobileOrTablet) {
                    navigate('/notifications');
                  } else {
                    setShowNotifDropdown((prev) => !prev);
                    if (!showNotifDropdown) fetchNotifications();
                  }
                }}
                aria-label="Notifications"
              >
                <Icon icon={bellOutline} width={18} height={18} />
                {unreadCount > 0 && (
                  <span className={styles.notifBadge}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Desktop dropdown */}
              {showNotifDropdown && !isMobileOrTablet && (
                <div className={styles.notifDropdown}>
                  <div className={styles.notifDropdownHeader}>
                    <span className={styles.notifDropdownTitle}>Notifications</span>
                    {notifications.unread.length > 0 && (
                      <button className={styles.notifMarkAll} onClick={markAllAsRead}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className={styles.notifDropdownList}>
                    {notifLoading ? (
                      <div className={styles.notifEmpty}>Loading...</div>
                    ) : notifications.unread.length === 0 && notifications.read.length === 0 ? (
                      <div className={styles.notifEmpty}>No notifications</div>
                    ) : (
                      <>
                        {notifications.unread.map((n) => (
                          <div key={n.id} className={`${styles.notifItem} ${styles.notifItemUnread}`}>
                            <div className={styles.notifDot} />
                            <div className={styles.notifItemContent}>
                              <div className={styles.notifItemTitle}>{n.title}</div>
                              <div className={styles.notifItemBody}>{n.body}</div>
                            </div>
                          </div>
                        ))}
                        {notifications.read.slice(0, 5).map((n) => (
                          <div key={n.id} className={styles.notifItem}>
                            <div className={styles.notifItemContent}>
                              <div className={styles.notifItemTitle}>{n.title}</div>
                              <div className={styles.notifItemBody}>{n.body}</div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  <button
                    className={styles.notifViewAll}
                    onClick={() => { setShowNotifDropdown(false); navigate('/notifications'); }}
                  >
                    View All
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Wallet (for logged in users, desktop only) */}
          {isLoggedIn && !isMobileOrTablet && <Wallet />}

          {/* Auth Buttons (for non-logged in users) */}
          {!isLoggedIn && (
            <div className={styles.authButtons}>
              <LoginButton />
              <RegisterButton />
            </div>
          )}

          {/* Action Icons (for non-logged in users) */}
          {/* {!isLoggedIn && !isMobile && (
            <div className={styles.iconGroup}>
              <button 
                className={styles.iconButton} 
                aria-label="Messages"
                onClick={handleSupportClick}
              >
                <ChatIcon size="24px" />
              </button>
              <button className={styles.iconButton} aria-label="Language">
                <GlobeIcon size="24px" />
              </button>
            </div>
          )} */}

          {/* Profile Menu (for logged in users - both mobile and desktop) */}
          {isLoggedIn && <ProfileMenu />}
        </div>
      </div>
    </header>
  );
};

export default Header;
